import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Switch } from 'antd';
import { colorGray, colorOrange } from '@atlas-frontend/design-tokens';
import {
  useInputProcessingEventStatusMutation,
  useSystemClassifierItems,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ToggleInputDataProcessingEventStatusSwitch.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'toggle-input-data-processing-event-status-switch'
);
addTranslations(translations);

export interface ToggleInputDataProcessingEventStatusSwitchProps {
  id: string;
  processingEventId: number;
  isResolved: boolean;
  isFlipped: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  onToggleStatus: (
    processingEventId: number,
    isResolved: boolean
  ) => Promise<unknown>;
}

export const ToggleInputDataProcessingEventStatusSwitch: FC<
  ToggleInputDataProcessingEventStatusSwitchProps
> = (props) => {
  const { t } = useTranslation();
  const {
    id,
    processingEventId,
    isResolved,
    onToggleStatus,
    isLoading,
    isFlipped,
    isDisabled,
  } = props;

  const handleClick = useCallback(async () => {
    await onToggleStatus(processingEventId, !isResolved);
  }, [processingEventId, isResolved]);

  return (
    <StyledSwitch
      id={id}
      checkedChildren={t('status.resolved')}
      unCheckedChildren={t('status.pending')}
      checked={isFlipped ? !isResolved : isResolved}
      onClick={handleClick}
      loading={isLoading}
      disabled={isDisabled}
      $noHandle={isDisabled}
    />
  );
};

const StyledSwitch = styled(Switch)<{ $noHandle: boolean }>`
  background-color: ${colorOrange[2]};

  &:hover:not(.ant-switch-disabled) {
    background-color: ${colorOrange[3]};
  }

  &.ant-switch-checked {
    background-color: ${colorGray[2]};

    &:hover:not(.ant-switch-disabled) {
      background-color: ${colorGray[4]};
    }
  }

  .ant-switch-inner {
    .ant-switch-inner-checked,
    .ant-switch-inner-unchecked {
      color: black;
    }
  }

  ${({ $noHandle }) =>
    $noHandle
      ? `
    .ant-switch-handle {
      display: none;
    }
    .ant-switch-inner {
      padding-inline: 9px !important;
    }
  `
      : ''}
`;

export interface ToggleProcessingEventStatusProps {
  idFlipped?: number;
  idInLimbo?: number;
  onToggle: (
    processingEventId: number,
    isResolved: boolean
  ) => Promise<unknown>;
}

export const useToggleProcessingEventStatus = (
  onSuccess: () => unknown,
  isUpdatingProcessingEvents: boolean
): ToggleProcessingEventStatusProps => {
  const { mutateAsync, variables, isPending, isSuccess } =
    useInputProcessingEventStatusMutation();

  const systemClassifierItems = useSystemClassifierItems('PROCESSING_STATUS', [
    'RESOLVED',
    'PENDING',
  ]);
  const resolvedId = systemClassifierItems[0]?.id;
  const pendingId = systemClassifierItems[1]?.id;

  if (!resolvedId) {
    throw new Error(
      'Expecting PROCESSING_STATUS system classifier item with code RESOLVED but did not find one'
    );
  }
  if (!pendingId) {
    throw new Error(
      'Expecting PROCESSING_STATUS system classifier item with code PENDING but did not find one'
    );
  }

  const handleToggleStatus = useCallback(
    async (processingEventId: number, isResolved: boolean) => {
      mutateAsync({
        id: processingEventId,
        statusId: isResolved ? resolvedId : pendingId,
      }).then(onSuccess);
    },
    [mutateAsync, onSuccess, resolvedId, pendingId]
  );

  const currentlyToggledId = variables?.id;

  return useMemo(
    () => ({
      onToggle: handleToggleStatus,
      idInLimbo: isPending ? currentlyToggledId : undefined,
      idFlipped:
        isSuccess && isUpdatingProcessingEvents
          ? currentlyToggledId
          : undefined,
    }),
    [isUpdatingProcessingEvents, isPending, isSuccess, currentlyToggledId]
  );
};
